import React from 'react';
import { observer } from 'mobx-react';
import { SvgIcon, Box, Text } from 'components';
import { ArrowUp, ArrowDown } from 'icons';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import color from 'styles/themes/wikifactory/styles/colors';
import {
  WallOffsetViewportStyled,
  WallOffsetPairStyled,
  WallOffsetPairDataStyled,
} from './wallOffsetPair.styled';

interface WallOffsetPairProps {
  offsetPair: WallOffsetPairObjProps;
  ix: number;
  icon?: React.ReactNode;
}

interface WallOffsetPairObjProps {
  // wasm [WallOffsetPair]
  selfIx: number;
  type: Object; // wasm enum WallOffsetType
  offset: number;
  minOffset: number;
  inspected: boolean;
  faceData: string;
}

export const WallOffsetPair = observer(
  ({ ix, icon, offsetPair }: WallOffsetPairProps) => {
    if (!offsetPair) return null;

    const viewerControl = useViewerControl();
    const selManager = viewerControl?.viewer?.selectionManager;
    if (!selManager) return null;

    const typeEnum = viewerControl?.viewer?.module.WallOffsetType;
    const { type, offset, minOffset, inspected } = offsetPair;
    const faceData = inspected ? JSON.parse(offsetPair?.faceData) : null;

    // const fIndex = `${offsetPair.selfIx}.`; // debug
    const fIndex = `${ix + 1}.`;
    const fOffset = `${offset.toFixed(1)}`;
    const fMinOffset = minOffset < offset && `clr. ${minOffset.toFixed(1)}`;

    let symbol = '';
    if (type === typeEnum.WallOffsetPlanar) symbol = '=';
    else if (type === typeEnum.WallOffsetSemiPlanar) symbol = '⋍';
    else if (type === typeEnum.WallOffsetConical) symbol = '⋐';
    else symbol = '≈';

    /*
      render
    */

    const selected = selManager.IsModelItemSelectionTarget(offsetPair);
    const highlighted = selManager.IsModelItemHighlightTarget(offsetPair);

    return (
      <WallOffsetViewportStyled>
        <WallOffsetPairStyled
          selected={selected}
          highlighted={highlighted}
          onMouseEnter={() => selManager.AddHighlightItem(offsetPair)}
          onMouseLeave={() => {
            selManager.ClearHighlight();
          }}
        >
          {icon && <SvgIcon icon={icon} size='medium' />}
          <Box
            mr={4}
            sx={{
              textAlign: 'right',
              color: color.wfGrey700,
              fontVariantNumeric: 'tabular-nums',
              fontSize: '14px',
            }}
          >
            {fIndex}
          </Box>
          <Box
            sx={{
              textAlign: 'center',
              color: color.wfGrey500,
              background: color.wfGrey100,
              borderRadius: '6px',
              border: `1px solid ${color.wfGrey500}`,
              fontWeight: '501',
            }}
          >
            {symbol}
          </Box>
          <Box ml={1} sx={{ color: color.wfBlack, textAlign: 'center' }}>
            {fOffset}
          </Box>
          <Box sx={{ color: color.wfGrey500 }}>{fMinOffset}</Box>

          <Box
            sx={{ margin: '0 0.5rem 0 auto' }}
            onClick={() => {
              // eslint-disable-next-line no-param-reassign
              offsetPair.inspected = !offsetPair.inspected;
            }}
          >
            <SvgIcon
              icon={!inspected ? <ArrowDown /> : <ArrowUp />}
              size='small'
            />
          </Box>
        </WallOffsetPairStyled>

        {/* face data */}
        {inspected && (
          <WallOffsetPairDataStyled
            selected={selected}
            highlighted={highlighted}
          >
            {Object.keys(faceData).map(key => {
              const label = key
                .replace('ol', 'overloads')
                .replace('cl', 'clearances');
              const faces = faceData[key];
              const faceStyles = {
                margin: '0 0 0 0.5rem',
                padding: '0 0.25rem',
                background: color.wfOrange.toString(),
                color: 'white',
                borderRadius: '5px',
                cursor: 'pointer',
              };
              if (key === 'ol') faceStyles.background = color.wfBlue600;
              else if (key === 'cl') faceStyles.background = color.wfPurple;

              return (
                <Box
                  key={`offsetData_${key}`}
                  sx={{
                    padding: '0.25rem 0',
                    borderLeft: `2px solid ${color.wfGrey500}`,
                  }}
                  mr={4}
                >
                  <Text
                    sx={{
                      fontWeight: 801,
                      marginLeft: '-2px',
                    }}
                  >{`- ${label}:`}</Text>
                  {faces.length > 0 ? (
                    faces.map((value: number) => {
                      return (
                        <Text key={`face_${value}`} sx={faceStyles}>
                          {value}
                        </Text>
                      );
                    })
                  ) : (
                    <Text key={`face_${faces}`} sx={faceStyles}>
                      {faces}
                    </Text>
                  )}
                </Box>
              );
            })}
          </WallOffsetPairDataStyled>
        )}
      </WallOffsetViewportStyled>
    );
  },
);
